import { env } from 'environments'
import { createPublicClient, formatUnits, http } from 'viem'
import { sepolia } from 'viem/chains'
import { useReadContract } from 'wagmi'

// addresses are from eth/usd from chainlink
// https://docs.chain.link/data-feeds/price-feeds/addresses?network=ethereum&page=1&search=eth%2F
const ETHEREUM_MAINNET_ETH_USD_PRICE_FEED_ADDRESS = '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419'
const SEPOLIA_ETH_USD_PRICE_FEED_ADDRESS = '0x694AA1769357215DE4FAC081bf1f309aDC325306'
const ETH_USD_PRICE_FEED_ADDRESS =
  env.chain.id === sepolia.id ? SEPOLIA_ETH_USD_PRICE_FEED_ADDRESS : ETHEREUM_MAINNET_ETH_USD_PRICE_FEED_ADDRESS

const AGGREGATOR_V3_INTERFACE = [
  {
    inputs: [],
    // uses the latestRoundData function from the Chainlink AggregatorV3Interface
    // https://docs.chain.link/data-feeds/using-data-feeds#javascript
    name: 'latestRoundData',
    outputs: [
      { internalType: 'uint80', name: 'roundId', type: 'uint80' },
      { internalType: 'int256', name: 'answer', type: 'int256' },
      { internalType: 'uint256', name: 'startedAt', type: 'uint256' },
      { internalType: 'uint256', name: 'updatedAt', type: 'uint256' },
      { internalType: 'uint80', name: 'answeredInRound', type: 'uint80' }
    ],
    stateMutability: 'view',
    type: 'function'
  }
] as const

export const useUSDToETH = (usd: number): { eth: number; ethPriceInUsd: number } => {
  const { data } = useReadContract({
    abi: AGGREGATOR_V3_INTERFACE,
    address: ETH_USD_PRICE_FEED_ADDRESS,
    functionName: 'latestRoundData',
    args: [],
    chainId: env.chainId
  })

  const ethPriceInUsd = Number(formatUnits(data?.[1] ?? BigInt(0), 8))

  return {
    eth: ethPriceInUsd > 0 ? usd / ethPriceInUsd : 0,
    ethPriceInUsd
  }
}

export const useETHToUSD = (eth: number): { usd: number; ethPriceInUsd: number } => {
  const { data } = useReadContract({
    abi: AGGREGATOR_V3_INTERFACE,
    address: ETH_USD_PRICE_FEED_ADDRESS,
    functionName: 'latestRoundData',
    args: [],
    chainId: env.chainId
  })
  const ethPriceInUsd = Number(formatUnits(data?.[1] ?? BigInt(0), 8))

  return {
    usd: ethPriceInUsd > 0 ? eth * ethPriceInUsd : 0,
    ethPriceInUsd
  }
}

export const getEthPriceInUsd = async (): Promise<number> => {
  const publicClient = createPublicClient({
    chain: env.chain,
    transport: http()
  })
  const data = await publicClient.readContract({
    abi: AGGREGATOR_V3_INTERFACE,
    address: ETH_USD_PRICE_FEED_ADDRESS,
    functionName: 'latestRoundData'
  })
  const ethPriceInUsd = Number(formatUnits(data?.[1] ?? BigInt(0), 8))
  return ethPriceInUsd
}

export const getUsdInEth = (usdAmt: number, ethPriceInUsd: number): number => {
  return Number((usdAmt / ethPriceInUsd).toFixed(4))
}
